import { useState, useRef, useEffect } from 'react'

type Props = {
  label?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  disabled?: boolean
  className?: string
}

export default function DatePicker({
  label,
  value,
  onChange,
  placeholder = 'yyyy-mm-dd',
  required,
  disabled,
  className,
}: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    if (value) {
      try {
        return new Date(value + 'T00:00:00')
      } catch {
        return new Date()
      }
    }
    return new Date()
  })
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleDateSelect = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    onChange(`${year}-${month}-${day}`)
    setIsOpen(false)
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev)
      if (direction === 'prev') {
        newMonth.setMonth(newMonth.getMonth() - 1)
      } else {
        newMonth.setMonth(newMonth.getMonth() + 1)
      }
      return newMonth
    })
  }

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startPadding = firstDay.getDay()
    const totalDays = lastDay.getDate()

    const days: (Date | null)[] = []

    // Add padding for days before the first of the month
    for (let i = 0; i < startPadding; i++) {
      days.push(null)
    }

    // Add all days of the month
    for (let i = 1; i <= totalDays; i++) {
      days.push(new Date(year, month, i))
    }

    return days
  }

  const calendarDays = generateCalendarDays()
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  let selectedDate: Date | null = null
  if (value) {
    try {
      selectedDate = new Date(value + 'T00:00:00')
      selectedDate.setHours(0, 0, 0, 0)
    } catch {
      selectedDate = null
    }
  }

  const displayValue = value || ''

  return (
    <div className={className} ref={containerRef}>
      {label ? (
        <label className="block text-sm font-medium mb-2">
          {label}
          {required ? <span className="text-red-500 ml-1">*</span> : null}
        </label>
      ) : null}
      
      <div className="relative">
        <input
          type="text"
          value={displayValue}
          onClick={() => !disabled && setIsOpen(true)}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={false}
          className="ds-input pr-10 cursor-pointer"
        />
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M5 11h14M6 21h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>

        {isOpen && !disabled && (
          <div className="absolute top-full left-0 mt-2 bg-white dark:bg-[rgb(var(--surface-rgb))] rounded-lg border border-gray-200 dark:border-[rgb(var(--border-rgb))] shadow-lg z-50 p-4 min-w-[320px]">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={() => navigateMonth('prev')}
                className="p-2 hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.06)] rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentMonth(new Date())}
                  className="text-sm font-medium text-gray-900 dark:text-[rgb(var(--text-rgb))] hover:text-gray-600 dark:hover:text-[rgb(var(--muted-rgb))] transition-colors"
                >
                  {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </button>
              </div>

              <button
                type="button"
                onClick={() => navigateMonth('next')}
                className="p-2 hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.06)] rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                <div key={index} className="text-center text-xs font-medium text-gray-400 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((date, index) => {
                if (!date) {
                  return <div key={`empty-${index}`} className="aspect-square" />
                }

                const isToday = date.toDateString() === today.toDateString()
                const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString()
                const isPast = date < today

                return (
                  <button
                    key={date.toISOString()}
                    type="button"
                    onClick={() => handleDateSelect(date)}
                    className={`
                      aspect-square flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-150
                      ${isSelected
                        ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                        : isToday
                        ? 'bg-gray-100 dark:bg-[rgba(255,255,255,0.10)] text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-[rgba(255,255,255,0.15)]'
                        : isPast
                        ? 'text-gray-400 dark:text-[rgb(var(--muted-rgb))] hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.04)]'
                        : 'text-gray-900 dark:text-[rgb(var(--text-rgb))] hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.06)]'
                      }
                    `}
                  >
                    {date.getDate()}
                  </button>
                )
              })}
            </div>

            {/* Quick Actions */}
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-[rgb(var(--border-rgb))]">
              <button
                type="button"
                onClick={() => {
                  const today = new Date()
                  handleDateSelect(today)
                }}
                className="flex-1 px-3 py-2 text-xs font-medium text-gray-600 dark:text-[rgb(var(--muted-rgb))] hover:text-gray-900 dark:hover:text-[rgb(var(--text-rgb))] hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.06)] rounded-lg transition-all"
              >
                Today
              </button>
              {value && (
                <button
                  type="button"
                  onClick={() => {
                    onChange('')
                    setIsOpen(false)
                  }}
                  className="flex-1 px-3 py-2 text-xs font-medium text-gray-600 dark:text-[rgb(var(--muted-rgb))] hover:text-gray-900 dark:hover:text-[rgb(var(--text-rgb))] hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.06)] rounded-lg transition-all"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
